import type { CronJobDef, CronScheduleDef, GencowCronDef, GencowProcedureDef } from "@gencow/core";
import { isCronJobsBuilder } from "@gencow/core";

function getApiCandidate(appModule: unknown): unknown {
  if (!appModule || typeof appModule !== "object") return undefined;
  const mod = appModule as { default?: unknown };
  return mod.default !== undefined && mod.default !== null && typeof mod.default === "object"
    ? mod.default
    : appModule;
}

function isInternalProcedureDef(value: unknown): value is GencowProcedureDef<"internal", any, any, any> {
  return (
    !!value &&
    typeof value === "object" &&
    (value as GencowProcedureDef).kind === "internal" &&
    typeof (value as GencowProcedureDef).name === "string"
  );
}

function assertNotUserProcedureRef(value: unknown, name: string): void {
  if (!value || typeof value !== "object") return;
  const kind = (value as { kind?: unknown }).kind;
  if (kind === "query" || kind === "mutation") {
    throw new Error(`defineApi.crons job "${name}" must reference procedure.internal, not procedure.${kind}`);
  }
}

function normalizeCronJob(job: CronJobDef | CronScheduleDef | GencowCronDef): CronScheduleDef | null {
  if ("procedureName" in job && typeof job.procedureName === "string") {
    return { name: job.name, pattern: job.pattern, procedureName: job.procedureName };
  }

  const action = (job as CronJobDef).action;
  if (typeof action === "function") {
    return null;
  }
  if (typeof action === "string") {
    return { name: job.name, pattern: job.pattern, procedureName: action };
  }
  if (isInternalProcedureDef(action)) {
    return { name: job.name, pattern: job.pattern, procedureName: action.name };
  }

  assertNotUserProcedureRef(action, job.name);
  throw new Error(`defineApi.crons job "${job.name}" must use a string, inline handler, or procedure.internal ref`);
}

function normalizeLocalCronJob(job: CronJobDef | CronScheduleDef | GencowCronDef): CronJobDef | null {
  if ("procedureName" in job && typeof job.procedureName === "string") {
    return { name: job.name, pattern: job.pattern, action: job.procedureName };
  }

  const action = (job as CronJobDef).action;
  if (typeof action === "function" || typeof action === "string") {
    return { name: job.name, pattern: job.pattern, action };
  }
  if (isInternalProcedureDef(action)) {
    return { name: job.name, pattern: job.pattern, action: action.name };
  }

  assertNotUserProcedureRef(action, job.name);
  throw new Error(`defineApi.crons job "${job.name}" must use a string, inline handler, or procedure.internal ref`);
}

export function hasDefineApiCrons(appModule: unknown): boolean {
  const apiCandidate = getApiCandidate(appModule);
  return !!apiCandidate && typeof apiCandidate === "object" && "crons" in apiCandidate;
}

function getCronJobs(appModule: unknown): unknown[] {
  const apiCandidate = getApiCandidate(appModule);
  if (!apiCandidate || typeof apiCandidate !== "object") return [];

  const crons = (apiCandidate as { crons?: unknown }).crons;
  if (!crons) return [];

  return flattenCronDefinitions(crons);
}

function flattenCronDefinitions(crons: unknown): unknown[] {
  if (!crons) return [];
  if (isCronJobsBuilder(crons)) return crons.getJobs();
  if (Array.isArray(crons)) return crons.flatMap((job) => flattenCronDefinitions(job));
  if (typeof crons === "object") {
    if ("name" in crons && "pattern" in crons) return [crons];
    return Object.values(crons).flatMap((job) => flattenCronDefinitions(job));
  }
  return [];
}

export function collectDefineApiCronJobs(appModule: unknown): CronJobDef[] {
  return getCronJobs(appModule)
    .map((job) => normalizeLocalCronJob(job as CronJobDef | CronScheduleDef | GencowCronDef))
    .filter((job): job is CronJobDef => job !== null);
}

export function collectCronSchedules(appModule: unknown): CronScheduleDef[] {
  const schedules = getCronJobs(appModule)
    .map((job) => normalizeCronJob(job as CronJobDef | CronScheduleDef | GencowCronDef))
    .filter((job): job is CronScheduleDef => job !== null);

  return schedules.sort((a, b) => a.name.localeCompare(b.name));
}
